// src/services/blogSearchService.js
import { blogService } from './blogService';

// Blog search index for instant filtering
class BlogSearchIndex {
  constructor() {
    this.blogs = [];
    this.searchableFields = ['title', 'excerpt', 'content', 'category', 'tags'];
    this.isLoaded = false;
    this.loadingPromise = null;
  }

  // Load all blogs into index
  async loadIndex() {
    if (this.isLoaded) return;
    if (this.loadingPromise) return this.loadingPromise;

    this.loadingPromise = (async () => {
      try {
        const response = await blogService.getBlogs({ page: 1, limit: 1000 });
        this.blogs = response.blogs || [];
        this.isLoaded = true;
        console.log(`Blog search index loaded: ${this.blogs.length} blogs`);
      } catch (error) {
        console.error('Failed to load blog index:', error);
        this.blogs = [];
        this.isLoaded = true;
      }
    })();

    return this.loadingPromise;
  }

  // Search blogs instantly
  search(query, options = {}) {
    const blogsToSearch = this.blogs || [];

    if (!query || query.trim() === '') {
      let results = [...blogsToSearch];
      const { category = null, tag = null } = options;

      if (category) {
        results = results.filter(blog => blog.category === category);
      }
      if (tag) {
        results = results.filter(blog => blog.tags && blog.tags.includes(tag));
      }
      results = results.filter(blog => blog.status === 'published');

      return this.applySortingAndPagination(results, options);
    }

    const searchTerm = query.toLowerCase().trim();
    const {
      category = null,
      tag = null,
      sortBy = 'publishedAt',
      sortOrder = 'desc',
      limit = 9,
      page = 1,
    } = options;

    let results = blogsToSearch.filter(blog => {
      let matchesSearch = this.searchableFields.some(field => {
        const value = blog[field];
        if (!value) return false;
        if (Array.isArray(value)) {
          return value.some(v => v && v.toLowerCase().includes(searchTerm));
        }
        return value.toString().toLowerCase().includes(searchTerm);
      });

      if (!matchesSearch) return false;
      if (category && blog.category !== category) return false;
      if (tag && (!blog.tags || !blog.tags.includes(tag))) return false;
      return blog.status === 'published';
    });

    return this.applySortingAndPagination(results, options);
  }

  applySortingAndPagination(results, options) {
    const {
      sortBy = 'publishedAt',
      sortOrder = 'desc',
      limit = 9,
      page = 1,
    } = options;

    const sortedResults = [...results].sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      if (sortBy === 'publishedAt' || sortBy === 'createdAt') {
        aVal = new Date(aVal || 0);
        bVal = new Date(bVal || 0);
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    const start = (page - 1) * limit;
    const paginatedResults = sortedResults.slice(start, start + limit);

    return {
      blogs: paginatedResults,
      totalItems: results.length,
      currentPage: page,
      totalPages: Math.ceil(results.length / limit) || 1,
      hasNext: start + limit < results.length,
      hasPrev: page > 1,
    };
  }

  getBlogBySlug(slug) {
    const blogs = this.blogs || [];
    return blogs.find(blog => blog.slug === slug);
  }

  getCategories() {
    const blogs = this.blogs || [];
    const categories = new Map();
    blogs.forEach(blog => {
      if (blog.category && blog.status === 'published') {
        categories.set(blog.category, (categories.get(blog.category) || 0) + 1);
      }
    });
    return Array.from(categories.entries()).map(([name, count]) => ({
      name,
      count,
    }));
  }

  getTags() {
    const blogs = this.blogs || [];
    const tags = new Map();
    blogs.forEach(blog => {
      if (blog.tags && blog.status === 'published') {
        blog.tags.forEach(tag => {
          if (tag) {
            tags.set(tag, (tags.get(tag) || 0) + 1);
          }
        });
      }
    });
    return Array.from(tags.entries()).map(([name, count]) => ({ name, count }));
  }

  clear() {
    this.blogs = [];
    this.isLoaded = false;
    this.loadingPromise = null;
  }

  isReady() {
    return this.isLoaded;
  }
}

// Singleton instance
const blogSearchIndex = new BlogSearchIndex();

// Initialize index in background
if (typeof window !== 'undefined') {
  setTimeout(() => {
    blogSearchIndex.loadIndex().catch(console.error);
  }, 100);
}

export default blogSearchIndex;
